import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { toast } from "react-toastify";
import { COLORS } from "../../assets/theme";
import {
  DeletionPreview,
  getDeletionPreview,
  hardDeleteOrganization,
  setOrganizationDeleted,
} from "../../services/superadminService";
import { getErrorMessage } from "../../utils/apiResponse";
import { SaCard, TONES } from "./saUi";
import ReportGmailerrorredOutlinedIcon from "@mui/icons-material/ReportGmailerrorredOutlined";

interface Props {
  organizationId: string;
  organizationName: string;
  isDeleted: boolean;
  onDeletedChange: (isDeleted: boolean) => void;
  /** After a hard delete: the organization no longer exists. */
  onRemoved: () => void;
}

/**
 * Soft delete closes the organization (nobody can sign in, staff or
 * patients) and can be undone. Hard delete is the second step, only for a
 * soft-deleted organization: it shows exactly what will go, and needs the
 * exact name typed.
 */
const OrganizationDangerZone: React.FC<Props> = ({
  organizationId,
  organizationName,
  isDeleted,
  onDeletedChange,
  onRemoved,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [confirmSoft, setConfirmSoft] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hardOpen, setHardOpen] = useState(false);
  const [preview, setPreview] = useState<DeletionPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [typedName, setTypedName] = useState("");

  const toggleSoft = async (deleted: boolean) => {
    setBusy(true);
    try {
      await setOrganizationDeleted(organizationId, deleted);
      onDeletedChange(deleted);
      toast.success(deleted ? "Organization closed." : "Organization restored.", {
        position: "top-right",
      });
      setConfirmSoft(false);
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not update the organization."), {
        position: "top-right",
      });
    }
    setBusy(false);
  };

  const openHard = async () => {
    setHardOpen(true);
    setTypedName("");
    setPreview(null);
    setLoadingPreview(true);
    try {
      setPreview(await getDeletionPreview(organizationId));
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not prepare the delete."), { position: "top-right" });
      setHardOpen(false);
    }
    setLoadingPreview(false);
  };

  const runHard = async () => {
    setBusy(true);
    try {
      const result = await hardDeleteOrganization(organizationId, typedName);
      toast.success(`${organizationName} was permanently deleted (${result.totalRows} rows).`, {
        position: "top-right",
      });
      setHardOpen(false);
      onRemoved();
    } catch (err) {
      toast.error(getErrorMessage(err, "Nothing was deleted."), { position: "top-right" });
    }
    setBusy(false);
  };

  const nameMatches = typedName.trim() === organizationName.trim();
  const blocked = (preview?.blockers.length ?? 0) > 0;

  const row = (title: string, body: string, action: React.ReactNode) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, flexWrap: "wrap" }}>
      <Box sx={{ flex: "1 1 260px" }}>
        <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>{title}</Typography>
        <Typography sx={{ fontSize: "0.8rem", color: COLORS.placeHolderColor }}>{body}</Typography>
      </Box>
      {action}
    </Box>
  );

  return (
    <>
      <SaCard
        danger
        title="Danger zone"
        subtitle="These lock people out of the organization or erase it."
        icon={<ReportGmailerrorredOutlinedIcon />}
        flush
      >
        {isDeleted
          ? row(
              "Restore organization",
              "Reopens it: staff and patients can sign in again, with all data as it was.",
              <Button
                variant="outlined"
                disabled={busy}
                onClick={() => toggleSoft(false)}
                sx={{ textTransform: "none", minHeight: 40 }}
              >
                Restore
              </Button>
            )
          : row(
              "Close organization (soft delete)",
              "Nobody can sign in or book any more, staff or patients. Nothing is erased; you can restore it.",
              <Button
                color="error"
                variant="outlined"
                disabled={busy}
                onClick={() => setConfirmSoft(true)}
                sx={{ textTransform: "none", minHeight: 40 }}
              >
                Close organization
              </Button>
            )}
        <Box sx={{ borderTop: `1px solid ${TONES.red.border}` }} />
        {row(
          "Delete permanently",
          isDeleted
            ? "Erases the organization and everything it owns. This cannot be undone."
            : "Close the organization first; permanent delete is the second step.",
          <Button
            color="error"
            variant="contained"
            disableElevation
            disabled={!isDeleted || busy}
            onClick={openHard}
            sx={{ textTransform: "none", minHeight: 40 }}
          >
            Delete permanently
          </Button>
        )}
      </SaCard>

      <Dialog open={confirmSoft} onClose={() => !busy && setConfirmSoft(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Close {organizationName}?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.9rem" }}>
            Everyone is signed out on their next action, and patients can no longer book. You can
            restore it at any time.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmSoft(false)} disabled={busy} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disableElevation
            disabled={busy}
            onClick={() => toggleSoft(true)}
            sx={{ textTransform: "none" }}
          >
            Close organization
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={hardOpen}
        onClose={() => !busy && setHardOpen(false)}
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete {organizationName} permanently?</DialogTitle>
        <DialogContent>
          {loadingPreview || !preview ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              {blocked ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {preview.blockers.join(" ")}
                </Alert>
              ) : (
                <Alert severity="error" sx={{ mb: 2 }}>
                  This erases {preview.totalRows.toLocaleString()} rows and cannot be undone. Sign-in
                  accounts shared with other organizations are kept.
                  {preview.keptInvoices > 0 &&
                    ` Its ${preview.keptInvoices} invoice${
                      preview.keptInvoices === 1 ? " is" : "s are"
                    } kept and will show as "${organizationName} (Deleted)".`}
                </Alert>
              )}
              <Box sx={{ maxHeight: 260, overflowY: "auto", border: `1px solid ${COLORS.BorderColor}`, borderRadius: 1, mb: 2 }}>
                <Table size="small">
                  <TableBody>
                    {preview.tables.map((t) => (
                      <TableRow key={t.table}>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{t.table}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                          {t.rows.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
              {!blocked && (
                <TextField
                  fullWidth
                  size="small"
                  label={`Type "${organizationName}" to confirm`}
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  autoComplete="off"
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setHardOpen(false)} disabled={busy} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disableElevation
            disabled={busy || blocked || !nameMatches || !preview}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={runHard}
            sx={{ textTransform: "none" }}
          >
            Delete permanently
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrganizationDangerZone;
